<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <nav class="navbar" role="navigation">
    <div class="navbar-brand">
      <router-link :to="projects" exact class="navbar-item">
        <div class="logo-container">
          <img src="@/assets/icon.svg" id="logo" alt="VidaPath">
          <h1 class="brand">VidaPath</h1>
        </div>
      </router-link>
    </div>
    <div id="topMenu" class="navbar-menu" :class="{ 'is-active': openedTopMenu }">
      <div v-if="!$keycloak.hasTemporaryToken" class="navbar-start">
        <!-- <navbar-dropdown
      icon="fa-folder-open"
      v-if="this.nbActiveProjects > 0"
      :title="$t('workspace')"
      :listPathes="['/project/']">
        <navigation-tree />
      </navbar-dropdown> -->
        <router-link to="/projects" class="navbar-item">
          <i class="fas fa-list-alt"></i>
          <span>{{ projectsLabel }}</span>
        </router-link>
        <router-link to="/ontology" class="navbar-item">
          <i class="fa fa-hashtag"></i>
          <span>{{ $t('terms') }}</span>
        </router-link>
        <router-link to="/storage" class="navbar-item">
          <i class="fas fa-upload"></i>
          <span>{{ $t('upload') }}</span>
        </router-link>
        <router-link to="/ai" class="navbar-item">
          <i class="fas fa-robot"></i>
          <span>AI</span>
        </router-link>
        <!-- <router-link v-if="currentUser.adminByNow" to="/admin" class="navbar-item">
        <i class="fas fa-wrench"></i>
        {{ $t('admin-menu') }}
      </router-link> -->
      </div>

      <div v-if="showPatientInfo" class="patient-info">
        <div class="patient-details">
          <span v-if="currentProject.patientName" class="patient-field">{{ $t('patient') }}: {{ currentProject.patientName }}</span>
          <span v-if="currentProject.patientId" class="patient-field">{{ $t('patient-id') }}: {{ currentProject.patientId }}</span>
          <span v-if="currentProject.patientAge" class="patient-field">{{ $t('age') }}: {{ currentProject.patientAge }}</span>
          <span v-if="currentProject.patientAge" class="patient-field">{{ $t('gender') }}: {{ currentProject.patientSex }}</span>
        </div>
      </div>

      <div class="navbar-end">
        <!-- 显示患者信息 -->

        <!-- <cytomine-searcher /> -->
        <!-- TODO IAM -->
        <navbar-dropdown v-if="!$keycloak.hasTemporaryToken" icon="fa-user" :title="currentUser.name"
          :listPathes="userDropdownPathes">
          <router-link to="/account" class="navbar-item">
            <i class="fas fa-user fa-xs"></i>
            {{ $t('account') }}
          </router-link>
          <router-link v-if="canSeeManagementMenu" to="/admin" class="navbar-item">
            <i class="fas fa-wrench fa-xs"></i>
            {{ $t('management') }}
          </router-link>
          <!-- <router-link to="/activity" class="navbar-item">
            <span class="icon"><i class="fas fa-history fa-xs"></i></span> {{ $t('activity-history') }}
          </router-link> -->
          <!-- <template v-if="currentUser.admin">
            <a v-if="!currentUser.adminByNow" class="navbar-item" @click="openAdminSession()">
              <span class="icon"><i class="fas fa-star fa-xs"></i></span> {{ $t('open-admin-session') }}
            </a>
            <a v-else class="navbar-item" @click="closeAdminSession()">
              <span class="icon"><i class="far fa-star fa-xs"></i></span> {{ $t('close-admin-session') }}
            </a>
          </template> -->
          <a class="navbar-item" @click="logout()">
            <i class="fas fa-power-off fa-xs"></i>
            {{ $t('logout') }}
          </a>
        </navbar-dropdown>

        <navbar-dropdown icon="fa-question-circle" :title="$t('help')">
          <a class="navbar-item" @click="openHotkeysModal()">
            <i class="far fa-keyboard fa-xs"></i>
            {{ $t('shortcuts') }}
          </a>
          <a class="navbar-item" href="/VidaPath_User_Guide.html" target="_blank" rel="noopener noreferrer">
            <i class="fas fa-book fa-xs"></i>
            {{ $t('user-guide') }}
          </a>
          <a class="navbar-item" @click="openAboutModal()">
            <i class="fas fa-info-circle fa-xs"></i>
            {{ $t('about') }}
          </a>
        </navbar-dropdown>
      </div>
    </div>
  </nav>
</template>

<script>
import { get } from '@/utils/store-helpers';
import { mapGetters } from 'vuex';
import { changeLanguageMixin } from '@/lang.js';

import NavbarDropdown from './NavbarDropdown';
import NavigationTree from './NavigationTree';
import HotkeysModal from './HotkeysModal';
import AboutCytomineModal from './AboutCytomineModal';
import CytomineSearcher from '@/components/search/CytomineSearcher';
import shortcuts from '@/utils/shortcuts.js';
import { findShortcutKey, preventShortcutEvent } from '@/utils/shortkey-events';

export default {
  name: 'cytomine-navbar',
  components: {
    NavbarDropdown,
    NavigationTree,
    CytomineSearcher
  },
  mixins: [changeLanguageMixin],
  data() {
    return {
      openedTopMenu: false,
      hotkeysModal: null,
      aboutModal: null
    };
  },
  computed: {
    ...mapGetters('serverConfig', ['isFolderBasedEnabled']),
    currentUser: get('currentUser/user'),
    currentProject: get('currentProject/project'),
    nbActiveProjects() {
      return Object.keys(this.$store.state.projects).length;
    },
    openHotkeysModalShortcut() {
      return shortcuts['general-shortcuts-modal'];
    },
    showPatientInfo() {
      // 检查是否在Viewer页面并且当前项目有患者信息
      return this.$route.path.includes('/image/') && this.currentProject &&
        (this.currentProject.patientName || this.currentProject.patientId || this.currentProject.patientAge) &&
        !this.$keycloak.hasTemporaryToken;
    },
    projects() {
      return this.$keycloak.hasTemporaryToken ? `/projects?access_token=${this.$keycloak.temporaryToken}` : '/projects';
    },
    projectsLabel() {
      // Dynamically return "Folders" or "Cases" based on server config
      return this.isFolderBasedEnabled ? this.$t('folders') : this.$t('cases');
    },
    canSeeManagementMenu() {
      return this.$keycloak.hasResourceRole('ADMIN');
    },
    userDropdownPathes() {
      return this.canSeeManagementMenu ? ['/account', '/admin'] : ['/account'];
    }
  },
  watch: {
    $route() {
      this.openedTopMenu = false;
    }
  },
  methods: {
    handleShortcutKeydown(event) {
      if (findShortcutKey(event, { openHotkeysModal: this.openHotkeysModalShortcut })) {
        preventShortcutEvent(event);
      }
    },
    handleShortcutKeyup(event) {
      if (findShortcutKey(event, { openHotkeysModal: this.openHotkeysModalShortcut })) {
        preventShortcutEvent(event);
        this.openHotkeysModal();
      }
    },
    // required to use programmatic modal for correct display in IE11
    openHotkeysModal() {
      if (!this.hotkeysModal) {
        this.hotkeysModal = this.$buefy.modal.open({
          parent: this,
          component: HotkeysModal,
          hasModalCard: true,
          canCancel: ['escape', 'outside'],
          onCancel: () => this.hotkeysModal = null,
        });
      }
    },
    openAboutModal() {
      this.$buefy.modal.open({
        parent: this,
        component: AboutCytomineModal,
        hasModalCard: true,
        canCancel: ['escape', 'outside']
      });
    },
    // ---

    async openAdminSession() {
      try {
        await this.$store.dispatch('currentUser/openAdminSession');
        this.$router.push('/admin');
      } catch (error) {
        console.log(error);
      }
    },
    async closeAdminSession() {
      try {
        await this.$store.dispatch('currentUser/closeAdminSession');
        if (this.$router.currentRoute.path === '/') {
          this.$router.push('/projects');
        } else {
          this.$router.push('/');
        }
      } catch (error) {
        console.log(error);
      }
    },

    async logout() {
      try {
        this.$store.dispatch('logout');
        this.changeLanguage();
        await this.$keycloak.logout();
      } catch (error) {
        console.log(error);
        this.$notify({ type: 'error', text: this.$t('notif-error-logout') });
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleShortcutKeydown, true);
    document.addEventListener('keyup', this.handleShortcutKeyup, true);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleShortcutKeydown, true);
    document.removeEventListener('keyup', this.handleShortcutKeyup, true);
  }
};
</script>

<style lang="scss">
@import '../../assets/styles/dark-variables.scss';

.logo-container {
  color: $navbar-text-color;
  display: flex;
  align-items: center;
  height: 34px;
  gap: 0.65rem;
}

.navbar {
  font-weight: 600;
  z-index: 500;
  min-height: 3.15rem;
  background:
    linear-gradient(180deg, rgba(222, 238, 255, 0.035), rgba(222, 238, 255, 0)),
    linear-gradient(180deg, #18263b, $navbar-bg-primary) !important;
  border-bottom: 1px solid rgba($primary, 0.18);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.13);
  color: $navbar-text-color !important;

  #logo {
    filter: drop-shadow(0 0 7px rgba($primary, 0.42));
  }

  .brand {
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: 0.04rem;
    text-transform: uppercase;
  }

  .fas,
  .far {
    padding-right: 0.5rem;
  }

  .navbar-brand {
    .brand {
      color: $navbar-text-color !important;
    }
  }

  .navbar-item,
  .navbar-link {
    background-color: transparent !important;
    color: $navbar-text-color !important;
    min-height: 3.15rem;
    transition: background-color 0.16s ease, box-shadow 0.16s ease, color 0.16s ease;
  }

  .navbar-item:hover,
  .navbar-link:hover {
    background-color: rgba($primary, 0.1) !important;
    box-shadow: inset 0 -2px 0 rgba($primary, 0.48);
    color: #b9f4fb !important;
  }

  .navbar-dropdown .navbar-item:hover {
    background-color: $navbar-bg-secondary !important;
    color: $navbar-text-color !important;
  }
}

.patient-info {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  padding-right: 3rem;
}

.patient-details {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  color: $dark-text-secondary;
  background: rgba(9, 20, 34, 0.42);
  border: 1px solid rgba($primary, 0.14);
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
}

.patient-field {
  font-weight: bold;
  font-size: 0.92rem;
  white-space: nowrap;
}

/* Special styling for IE */
@media screen and (-ms-high-contrast: active),
(-ms-high-contrast: none) {
  #logo {
    height: 40px;
    max-height: none;
  }
}
</style>
